import type {
  GetServerSidePropsContext,
  NextApiRequest,
  NextApiResponse,
} from 'next'
import { useRouter } from 'next/router'
import Cookie from 'cookie-universal'
import { z } from 'zod'
import { useEffect } from 'react'

import type { Course } from '@mc/api'
import { track } from '@mc/track-event'
import { useStaticData } from '@mc/static-data-react'

import { ONE_MONTH } from 'libs/constants'
import { getExperimentVariables } from '@mc/next-growthbook'

// https://app.growthbook.io/features/cm-dynamic-hero-values
const DYNAMIC_CM_FEATURE_NAME = 'cm-dynamic-hero-values'

export type DynamicHeroType = NonNullable<
  z.infer<typeof DynamicHeroDataSchema>['dynamicHero']
>[string]

export type DynamicHeroValues = {
  header?: string | null
  subheader?: string | null
  nameplate?: string | null
  trailerMediaUuid?: string | null
}

type UtmKeyToPropertyNameMapType = {
  utmPropertyIndicator: string
  propertyName: string
}

type UTMContent = ReturnType<typeof useRouter>['query'][string]

type TrackViewedCMHeaderProps = {
  title: string
  subtitle: string
  nameplate: string
  trailerMediaUuid?: string
  isDynamicHero: boolean
  primaryCategory: number
}

const DynamicHeroDataSchema = z.object({
  campaignId: z.number().optional(),
  dynamicHero: z
    .record(
      z.object({
        header: z.record(z.string()).optional(),
        subheader: z.record(z.string()).optional(),
        nameplate: z.record(z.string()).optional(),
        trailerMediaUuid: z.record(z.string()).optional(),
      }),
    )
    .optional(),
})

const DynamicHeroValueSchema = z.object({
  header: z.string().nullable().optional(),
  subheader: z.string().nullable().optional(),
  nameplate: z.string().nullable().optional(),
  trailerMediaUuid: z.string().nullable().optional(),
})

const utmKeyToPropertyNameMap = [
  { utmPropertyIndicator: 'head_', propertyName: 'headerKey' },
  { utmPropertyIndicator: 'subhead_', propertyName: 'subheaderKey' },
  { utmPropertyIndicator: 'nameplate_', propertyName: 'nameplateKey' },
  {
    utmPropertyIndicator: 'trailerMediaUuid_',
    propertyName: 'trailerMediaUuidKey',
  },
]

const UTM_CONTENT_SEPARATOR = /\s|\+/

export const getDynamicValuesFromUtmContent = (utmContent: UTMContent) => {
  if (!utmContent || typeof utmContent !== 'string') return null
  const keys = utmContent.split(UTM_CONTENT_SEPARATOR)

  const {
    headerKey,
    subheaderKey,
    imageKey,
    mobileImageKey,
    nameplateKey,
    trailerMediaUuidKey,
  } = keys.reduce((acc: Record<string, string | undefined>, key) => {
    const item = utmKeyToPropertyNameMap.find((f) =>
      key.startsWith(f.utmPropertyIndicator),
    ) as UtmKeyToPropertyNameMapType
    if (!item) return acc
    acc[item.propertyName] = key.replace(item.utmPropertyIndicator, '')
    return acc
  }, {})

  return {
    headerKey,
    subheaderKey,
    imageKey,
    mobileImageKey,
    nameplateKey,
    trailerMediaUuidKey,
  }
}

export const getDynamicDataFromUtmContent = (
  ctx: GetServerSidePropsContext,
  dynamicHero: DynamicHeroType | null,
) => {
  const dynamicValues = getDynamicValuesFromUtmContent(ctx?.query?.utm_content)
  if (!dynamicValues)
    return {
      header: null,
      subheader: null,
      nameplate: null,
      trailerMediaUuid: null,
    }
  const { headerKey, subheaderKey, nameplateKey, trailerMediaUuidKey } =
    dynamicValues

  const header = dynamicHero?.header?.[headerKey as string] || null
  const subheader = dynamicHero?.subheader?.[subheaderKey as string] || null
  const nameplate = dynamicHero?.nameplate?.[nameplateKey as string] || null
  const trailerMediaUuid =
    dynamicHero?.trailerMediaUuid?.[trailerMediaUuidKey as string] || null

  return { header, subheader, nameplate, trailerMediaUuid }
}

export const useDynamicHero = (
  dynamicHero: DynamicHeroType | null | undefined,
  defaultValues: DynamicHeroValues,
  shouldDisplayDynamicHero = true,
) => {
  const { query } = useRouter()

  if (!shouldDisplayDynamicHero) return defaultValues

  const dynamicValues = getDynamicValuesFromUtmContent(query?.utm_content)
  if (!dynamicValues) return defaultValues
  const { headerKey, subheaderKey, nameplateKey, trailerMediaUuidKey } =
    dynamicValues

  const header =
    dynamicHero?.header?.[headerKey as string] || defaultValues?.header
  const subheader =
    dynamicHero?.subheader?.[subheaderKey as string] || defaultValues?.subheader
  const nameplate =
    dynamicHero?.nameplate?.[nameplateKey as string] || defaultValues?.nameplate
  const trailerMediaUuid =
    dynamicHero?.trailerMediaUuid?.[trailerMediaUuidKey as string] ||
    defaultValues?.trailerMediaUuid

  return { header, subheader, nameplate, trailerMediaUuid }
}

export const getDynamicHeroValue = async (ctx: GetServerSidePropsContext) => {
  const data = await getExperimentVariables(ctx, DYNAMIC_CM_FEATURE_NAME)
  const parsed = DynamicHeroDataSchema.safeParse(data)
  if (parsed.success) return parsed.data
  return undefined
}

const useDynamicHeroStaticData = () => {
  const { course, dynamicHero, savedDynamicHero } = useStaticData<{
    course: Course
    dynamicHero: DynamicHeroType | null | undefined
    savedDynamicHero: DynamicHeroValues
  }>()

  return { course, dynamicHero, savedDynamicHero }
}

type UseCMDynamicHeroOpts = {
  /**
   * If true, then default values fetched from the class data will be returned
   * (preserving existing behavior).
   *
   * If false, then dynamic hero values will be returned only if they are
   * present (in the UTM query string or in the saved dynamic CM cookie).
   * This makes it easier to use custom fallbacks.
   */
  disableDefaultValues?: boolean
}
export const useCMDynamicHero = (opts: UseCMDynamicHeroOpts = {}) => {
  const { course, dynamicHero, savedDynamicHero } = useDynamicHeroStaticData()

  const shouldDisplayDynamicHero = useShouldDisplayCMDynamicHero()

  const defaultHeroValues = {
    header: savedDynamicHero?.header || course?.headline,
    subheader: savedDynamicHero?.subheader || course?.marketingCmColumn,
    nameplate: savedDynamicHero?.nameplate || course?.nameplate,
    trailerMediaUuid: savedDynamicHero?.trailerMediaUuid || course?.mediaUuid,
  }
  const { header, subheader, nameplate, trailerMediaUuid } = useDynamicHero(
    dynamicHero,
    defaultHeroValues,
    shouldDisplayDynamicHero,
  )

  useEffect(() => {
    if (shouldDisplayDynamicHero) {
      trackViewedCMHeader({
        title: header as string,
        subtitle: subheader as string,
        nameplate: nameplate as string,
        trailerMediaUuid: trailerMediaUuid as string,
        isDynamicHero:
          !!dynamicHero?.header ||
          !!dynamicHero?.subheader ||
          !!dynamicHero?.nameplate ||
          !!dynamicHero?.trailerMediaUuid,
        primaryCategory: course.primaryCategory?.id as number,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!shouldDisplayDynamicHero && opts?.disableDefaultValues) {
    return {}
  }

  return { header, subheader, nameplate, trailerMediaUuid }
}

const useShouldDisplayCMDynamicHero = () => {
  const { query } = useRouter()
  const { savedDynamicHero, dynamicHero } = useDynamicHeroStaticData()
  const utmContent = query?.utm_content

  if (!dynamicHero) return false

  const utmDynamicVals = getDynamicValuesFromUtmContent(utmContent)
  if (utmDynamicVals) {
    const hasVals = Object.values(utmDynamicVals).some(
      (v) => typeof v === 'string' && v.length > 0,
    )
    if (hasVals) return true
  }

  if (
    savedDynamicHero?.header ||
    savedDynamicHero?.subheader ||
    savedDynamicHero?.nameplate ||
    savedDynamicHero?.trailerMediaUuid
  )
    return true

  return false
}

const VISITED_CM_DYNAMIC_COOKIE_PREFIX = 'cm_dynamic_hero_'
const COOKIE_OPTIONS = {
  maxAge: ONE_MONTH,
  path: '/',
}

interface DynamicData {
  header?: string | null
  subheader?: string | null
  nameplate?: string | null
  trailerMediaUuid?: string | null
}

export const saveCMDynamicHero = (
  req: Pick<NextApiRequest, 'headers'>,
  res: Pick<NextApiResponse, 'getHeaders'>,
  {
    slug,
    dynamicData,
  }: {
    slug: string
    dynamicData: DynamicData
  },
) => {
  const cookies = Cookie(req, res)

  cookies.set(
    VISITED_CM_DYNAMIC_COOKIE_PREFIX + slug,
    JSON.stringify(dynamicData),
    COOKIE_OPTIONS,
  )
}

export const getSavedCMDynamicHero = (
  req: Pick<NextApiRequest, 'headers'>,
  res: Pick<NextApiResponse, 'getHeaders'>,
  slug: string,
) => {
  const cookies = Cookie(req, res)

  const value = cookies.get(VISITED_CM_DYNAMIC_COOKIE_PREFIX + slug) || {}
  const parsed = DynamicHeroValueSchema.safeParse(value)
  if (parsed.success) return parsed.data
  return {}
}

export const trackViewedCMHeader = ({
  title,
  subtitle,
  nameplate,
  trailerMediaUuid,
  isDynamicHero,
  primaryCategory,
}: TrackViewedCMHeaderProps) => {
  track('Viewed CM Header', {
    title,
    subtitle,
    nameplate,
    trailerMediaUuid,
    context: {
      dynamic_header: isDynamicHero,
    },
    p1: primaryCategory,
  })
}
